// ESC/POS pure byte-stream renderer for Star TSP143IV-BI (80mm thermal).
// No browser or Node APIs — pure Uint8Array manipulation.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReceiptInput {
  account: {
    name: string;
    brandPrimaryColour?: string;
    /** Opt-in tax engine (migration 0088). Label printed on the tax line +
     * the "<LABEL> No: ..." reg-number line. Defaults to 'Tax' when a
     * tax amount is present but no label is set. */
    taxLabel?: string | null;
    /** VAT/Tax registration number — legal requirement on VAT receipts.
     * Printed only when set AND the transaction carries tax. */
    taxRegistrationNumber?: string | null;
  };
  facility: { name: string; addressLine: string };
  transaction: {
    id: string;
    total_minor: number;
    tax_minor: number | null;
    /** Optional pre-tax subtotal. When present and tax > 0, the receipt prints
     * a Subtotal + tax breakdown above the TOTAL. */
    subtotal_minor?: number;
    currency: string;
  };
  lines: Array<{
    description: string;
    quantity: number;
    unit_price_minor: number;
    line_total_minor: number;
  }>;
  payments: Array<{ source: string; amount_minor: number }>;
  staffName: string;
  printedAt: string; // ISO
}

export interface SessionTotals {
  opened_at: string;
  closed_at: string;
  opening_float_minor: number;
  closing_float_minor: number;
  expected_cash_minor: number;
  cash_variance_minor: number;
  totals_by_source: Record<string, number>;
  currency: string;
}

// ---------------------------------------------------------------------------
// ESC/POS command constants
// ---------------------------------------------------------------------------

const CMD_INIT = [0x1b, 0x40]; // ESC @
const CMD_ALIGN_CENTRE = [0x1b, 0x61, 0x01]; // ESC a 1
const CMD_ALIGN_LEFT = [0x1b, 0x61, 0x00]; // ESC a 0
const CMD_DOUBLE_SIZE = [0x1b, 0x21, 0x30]; // ESC ! 0x30 (double width + height)
const CMD_NORMAL_SIZE = [0x1b, 0x21, 0x00]; // ESC ! 0
const CMD_CUT = [0x1d, 0x56, 0x42, 0x00]; // GS V 0 0 (partial cut)

const RECEIPT_WIDTH = 42;

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function feedLines(n: number): Uint8Array {
  return new Uint8Array([0x1b, 0x64, n]);
}

function newline(): Uint8Array {
  return new Uint8Array([0x0a]);
}

function asciiBytes(s: string): Uint8Array {
  // Replace non-ASCII (>127) with '?' — printer doesn't render multibyte chars.
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    out[i] = code < 128 ? code : 0x3f; // '?'
  }
  return out;
}

function bytes(...parts: Array<number | number[] | Uint8Array>): Uint8Array {
  const arrays: Uint8Array[] = parts.map((p) => {
    if (typeof p === 'number') return new Uint8Array([p]);
    if (Array.isArray(p)) return new Uint8Array(p);
    return p;
  });
  const totalLen = arrays.reduce((sum, a) => sum + a.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const a of arrays) {
    result.set(a, offset);
    offset += a.length;
  }
  return result;
}

function text(s: string): Uint8Array {
  return bytes(asciiBytes(s), newline());
}

function divider(width = RECEIPT_WIDTH): Uint8Array {
  return bytes(asciiBytes('-'.repeat(width)), newline());
}

/**
 * Pad left + right strings with spaces so the combined string is exactly `width` chars.
 * If the combined text already exceeds width, truncate the right string.
 */
function pad(left: string, right: string, width = RECEIPT_WIDTH): Uint8Array {
  const safeLeft = left.slice(0, width);
  const rightMaxLen = width - safeLeft.length - 1;
  const safeRight = rightMaxLen > 0 ? right.slice(0, rightMaxLen) : '';
  const spaces = width - safeLeft.length - safeRight.length;
  const line = safeLeft + ' '.repeat(Math.max(0, spaces)) + safeRight;
  return bytes(asciiBytes(line), newline());
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function formatMoney(minor: number, currency: string): string {
  const sym = CURRENCY_SYMBOLS[currency] ?? currency;
  const major = (minor / 100).toFixed(2);
  return `${sym}${major}`;
}

/**
 * Wrap a string to lines of at most `width` characters (word-aware).
 * Returns array of lines (each already truncated to width).
 */
function wrapText(s: string, width = RECEIPT_WIDTH): string[] {
  // Replace non-ASCII with '?' first so length matches rendered bytes
  const safe = s.replace(/[^\x00-\x7f]/g, '?');
  if (safe.length <= width) return [safe];

  const lines: string[] = [];
  let remaining = safe;
  while (remaining.length > width) {
    // Find last space within width
    let breakAt = remaining.lastIndexOf(' ', width);
    if (breakAt <= 0) breakAt = width; // hard break
    lines.push(remaining.slice(0, breakAt));
    remaining = remaining.slice(breakAt).replace(/^ /, '');
  }
  if (remaining.length > 0) lines.push(remaining);
  return lines;
}

// ---------------------------------------------------------------------------
// Public: renderReceipt
// ---------------------------------------------------------------------------

export function renderReceipt(input: ReceiptInput): Uint8Array {
  const { account, facility, transaction, lines, payments, staffName, printedAt } = input;
  const currency = transaction.currency;

  // Format date from ISO string
  const dateStr = printedAt.replace('T', ' ').slice(0, 16);
  const saleId = transaction.id.slice(-8).toUpperCase();

  const parts: Uint8Array[] = [];

  // Initialize
  parts.push(bytes(CMD_INIT));

  // Header — centred, double-size account name
  parts.push(bytes(CMD_ALIGN_CENTRE));
  parts.push(bytes(CMD_DOUBLE_SIZE));
  parts.push(text(account.name));
  parts.push(bytes(CMD_NORMAL_SIZE));

  // Facility
  parts.push(text(facility.name));
  parts.push(text(facility.addressLine));
  parts.push(newline());

  // Meta — left aligned
  parts.push(bytes(CMD_ALIGN_LEFT));
  parts.push(text(`Sale ID: ${saleId}`));
  parts.push(text(`Date: ${dateStr}`));
  parts.push(text(`Staff: ${staffName}`));
  parts.push(newline());

  // Lines
  for (const line of lines) {
    const descLines = wrapText(line.description, RECEIPT_WIDTH);
    const totalStr = formatMoney(line.line_total_minor, currency);
    const unitStr = formatMoney(line.unit_price_minor, currency);
    const subLabel = `  ${line.quantity} x ${unitStr}`;

    // First description line padded with total
    parts.push(pad(descLines[0] ?? '', totalStr));
    // Subsequent description lines (wrapped)
    for (let i = 1; i < descLines.length; i++) {
      parts.push(text(descLines[i] ?? ''));
    }
    // Quantity x unit price on indent
    parts.push(text(subLabel));
  }

  parts.push(newline());
  parts.push(divider());

  // Tax breakdown — only when the transaction actually carries tax. When
  // tax_minor is 0/null (tax disabled, or zero-rated) we print NOTHING here,
  // keeping receipts byte-identical to the pre-tax-engine behaviour.
  const taxMinor = transaction.tax_minor ?? 0;
  if (taxMinor > 0) {
    const taxLabel = (input.account.taxLabel ?? '').trim() || 'Tax';
    parts.push(bytes(CMD_ALIGN_LEFT));
    // Subtotal line when the caller supplied a pre-tax subtotal.
    if (typeof transaction.subtotal_minor === 'number') {
      parts.push(pad('Subtotal', formatMoney(transaction.subtotal_minor, currency)));
    }
    parts.push(pad(taxLabel, formatMoney(taxMinor, currency)));
  }

  parts.push(newline());

  // Total — centred
  parts.push(bytes(CMD_ALIGN_CENTRE));
  parts.push(text('TOTAL'));
  parts.push(bytes(CMD_DOUBLE_SIZE));
  parts.push(text(formatMoney(transaction.total_minor, currency)));
  parts.push(bytes(CMD_NORMAL_SIZE));

  // Tax registration number — legal requirement on VAT receipts. Printed only
  // when set AND the transaction carries tax.
  if (taxMinor > 0) {
    const regNo = (input.account.taxRegistrationNumber ?? '').trim();
    if (regNo) {
      const taxLabel = (input.account.taxLabel ?? '').trim() || 'Tax';
      parts.push(bytes(CMD_ALIGN_CENTRE));
      parts.push(text(`${taxLabel} No: ${regNo}`));
    }
  }

  // Payments — left
  parts.push(bytes(CMD_ALIGN_LEFT));
  parts.push(newline());
  for (const payment of payments) {
    parts.push(pad(payment.source, formatMoney(payment.amount_minor, currency)));
  }

  parts.push(newline());

  // Footer
  parts.push(bytes(CMD_ALIGN_CENTRE));
  parts.push(text('Thank you!'));
  parts.push(newline());

  // Feed + cut
  parts.push(feedLines(4));
  parts.push(bytes(CMD_CUT));

  return bytes(...parts);
}

// ---------------------------------------------------------------------------
// Public: renderZReport
// ---------------------------------------------------------------------------

export function renderZReport(
  account: ReceiptInput['account'],
  facility: ReceiptInput['facility'],
  totals: SessionTotals,
): Uint8Array {
  const currency = totals.currency;

  const parts: Uint8Array[] = [];

  // Initialize
  parts.push(bytes(CMD_INIT));

  // Header — centred, double-size
  parts.push(bytes(CMD_ALIGN_CENTRE));
  parts.push(bytes(CMD_DOUBLE_SIZE));
  parts.push(text('Z REPORT'));
  parts.push(bytes(CMD_NORMAL_SIZE));
  parts.push(text(account.name));
  parts.push(text(facility.name));

  // Details — left
  parts.push(bytes(CMD_ALIGN_LEFT));
  parts.push(newline());
  parts.push(text(`Opened: ${totals.opened_at.replace('T', ' ').slice(0, 16)}`));
  parts.push(text(`Closed: ${totals.closed_at.replace('T', ' ').slice(0, 16)}`));
  parts.push(newline());

  const varianceSign = totals.cash_variance_minor >= 0 ? '+' : '-';
  const varianceAbs = Math.abs(totals.cash_variance_minor);

  parts.push(text(`Opening float: ${formatMoney(totals.opening_float_minor, currency)}`));
  parts.push(text(`Expected cash: ${formatMoney(totals.expected_cash_minor, currency)}`));
  parts.push(text(`Closing float: ${formatMoney(totals.closing_float_minor, currency)}`));
  parts.push(text(`Cash variance: ${varianceSign}${formatMoney(varianceAbs, currency)}`));
  parts.push(newline());

  parts.push(divider());

  parts.push(text('TOTALS BY SOURCE:'));
  for (const [source, amount] of Object.entries(totals.totals_by_source)) {
    parts.push(pad(`  ${source}`, formatMoney(amount, currency)));
  }

  parts.push(newline());

  // Feed + cut
  parts.push(feedLines(4));
  parts.push(bytes(CMD_CUT));

  return bytes(...parts);
}
